import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { PageSidesheetActions } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { LegalNitrogenWorkingCoefficient, LegalNitrogenWorkingCoefficientsFilter, SiteState } from 'src/app/shared/models';
import {
  getCropAids,
  getFertilizationSalesOrderPeriods,
  getLegalNitrogenWorkingCoefficients,
  getLegalNitrogenWorkingCoefficientsFilter,
  getLegalNitrogenWorkingCoefficientsNumberOfItems,
  getSoilTypes,
} from 'src/app/store/selectors';

import * as AppActions from '../../../store/actions';
import {
  AddLegalNitrogenWorkingCoefficient,
  ClearLegalNitrogenWorkingCoefficientsFilter,
  DeleteLegalNitrogenWorkingCoefficient,
  EditLegalNitrogenWorkingCoefficient,
  LoadLegalNitrogenWorkingCoefficientsRequest,
  UpdateLegalNitrogenWorkingCoefficientsFilter,
} from '../../../store/actions';

@Component({
  selector: 'app-legal-nitrogen-working-coefficient-list',
  templateUrl: './legal-nitrogen-working-coefficient-list.component.html',
  standalone: false,
})
export class LegalNitrogenWorkingCoefficientListComponent implements OnInit, OnDestroy {
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  request: {
    pageNumber: number;
    pageSize: number;
    filter: LegalNitrogenWorkingCoefficientsFilter;
  } = {
    pageNumber: 1,
    pageSize: 10,
    filter: {},
  };
  isMobile$ = this.breakpoint.ltLg$;
  coefficients$ = this.store.pipe(select(getLegalNitrogenWorkingCoefficients));
  soilTypes$ = this.store.pipe(select(getSoilTypes));
  cropAids$ = this.store.pipe(select(getCropAids));
  salesOrderPeriods$ = this.store.pipe(select(getFertilizationSalesOrderPeriods));
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'calendarYear',
        label: this.locale.translate('year'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'salesOrderPeriodName',
        label: this.locale.translate('salesOrderPeriod'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'fertilizerTypeValue',
        label: this.locale.translate('fertilizerType'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'cropAidName',
        label: this.locale.translate('cropAid'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'soilType',
        label: this.locale.translate('soilType'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'nitrogenWorkingCoefficient',
        label: this.locale.translate('legalNitrogenWorkingCoefficient'),
        type: TableColumnTypes.NUMBER,
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        icon: 'edit',
        callback: (event: LegalNitrogenWorkingCoefficient) => this.editItem(event),
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        icon: 'delete',
        callback: (event: LegalNitrogenWorkingCoefficient) => this.deleteItem(event),
        disabled: () => false,
      },
    ],
  };
  totalNumberOfItems$ = this.store.pipe(select(getLegalNitrogenWorkingCoefficientsNumberOfItems));
  filterControl = new FormControl();
  filterValue$ = this.filterControl.valueChanges.pipe(
    map((value: string) => value || ''),
    startWith(''),
  );
  filterEnabled$ = new BehaviorSubject(false);
  isEmpty$ = combineLatest([this.filterEnabled$, this.totalNumberOfItems$]).pipe(
    map(([filterEnabled, totalNumberOfItems]) => !filterEnabled && totalNumberOfItems === 0),
  );
  legalNitrogenWorkingCoefficientsFilter$ = this.store.pipe(select(getLegalNitrogenWorkingCoefficientsFilter));
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  ngOnInit() {
    combineLatest([this.coefficients$, this.soilTypes$, this.cropAids$, this.salesOrderPeriods$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([coefficients, soilTypes, cropAids, salesOrderPeriods]) => {
        if (!(coefficients.length && soilTypes?.length && cropAids?.length && salesOrderPeriods?.length)) {
          return;
        }
        this.tableConfig.dataSource.data = coefficients.map((coefficent) => {
          const soilType = soilTypes.find((s) => s.id === coefficent.soilTypeId)?.name;
          const salesOrderPeriodName = salesOrderPeriods.find((p) => p.id === coefficent.salesOrderPeriodId)?.name;
          const fertilizerTypeValue = this.locale.translate(coefficent.fertilizerType);
          const cropAidName = cropAids.find((c) => c.id === coefficent.cropAidId)?.name;
          return {
            ...coefficent,
            soilType,
            salesOrderPeriodName,
            fertilizerTypeValue,
            cropAidName,
          };
        });
      });

    this.updateData();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updateData() {
    this.store.dispatch(new LoadLegalNitrogenWorkingCoefficientsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  updateFilter(data: LegalNitrogenWorkingCoefficientsFilter) {
    this.request.filter = data;
    this.request.pageNumber = 1;
    this.store.dispatch(new UpdateLegalNitrogenWorkingCoefficientsFilter(data));
    this.store.dispatch(new LoadLegalNitrogenWorkingCoefficientsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  setFilterEnabled(isEnabled: boolean) {
    this.filterEnabled$.next(isEnabled);
  }

  resetFilter() {
    this.store.dispatch(new ClearLegalNitrogenWorkingCoefficientsFilter());
  }

  setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  addItem() {
    this.store.dispatch(new AddLegalNitrogenWorkingCoefficient());
  }

  editItem(item: LegalNitrogenWorkingCoefficient) {
    this.store.dispatch(new EditLegalNitrogenWorkingCoefficient(item));
  }

  deleteItem(item: LegalNitrogenWorkingCoefficient) {
    this.dialog
      .confirm({
        title: this.locale.translate('delete'),
        text: this.locale.translate('deleteItemWarning'),
        pushHistoryState: false,
      })
      .pipe(take(1))
      .subscribe((x) => {
        if (x.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(new DeleteLegalNitrogenWorkingCoefficient(item));
          this.updateData();
        }
      });
  }

  importItems() {
    this.store.dispatch(new AppActions.OpenLegalNitrogenWorkingCoefficientImportForm());
  }
}
